/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { uploadsAPI } from "@app/data/api/uploads";
import type { MenuPdfItem } from "@lemmonhq/types";

const ASSET_TYPE = "menu_pdf";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

export interface MenuPdfsProps {
  items: MenuPdfItem[];
  onUpdate: (items: MenuPdfItem[]) => void;
  onAssetUploaded: (assetId: string) => void;
}

export const MenuPdfs = defineComponent<MenuPdfsProps>({
  name: "MenuPdfs",
  props: ["items", "onUpdate", "onAssetUploaded"],
  setup(props) {
    const { t } = useLocalization();
    const newName = ref("");
    const fileInput = ref<HTMLInputElement | null>(null);
    const uploading = ref(false);
    const uploadError = ref("");
    const deleteConfirmIndex = ref<number | null>(null);

    const handleAdd = async () => {
      uploadError.value = "";
      const input = fileInput.value;
      const file = input?.files?.[0];

      if (!newName.value.trim()) {
        uploadError.value = t("settings.menu_pdfs.error_name_required");
        return;
      }
      if (!file) {
        uploadError.value = t("settings.menu_pdfs.error_file_required");
        return;
      }
      if (file.type !== "application/pdf") {
        uploadError.value = t("settings.menu_pdfs.error_invalid_type");
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        uploadError.value = t("settings.menu_pdfs.error_file_too_large");
        return;
      }

      try {
        uploading.value = true;
        const { url, assetId } = await uploadsAPI.uploadAsset(file, ASSET_TYPE);
        props.onAssetUploaded(assetId);
        props.onUpdate([
          ...props.items,
          { name: newName.value.trim(), url, assetId },
        ]);
        newName.value = "";
        if (input) input.value = "";
      } catch (error) {
        console.error("Failed to upload menu pdf:", error);
        uploadError.value = t("settings.menu_pdfs.error_upload_failed");
      } finally {
        uploading.value = false;
      }
    };

    const handleDeleteClick = (index: number) => {
      deleteConfirmIndex.value = index;
    };

    const confirmDelete = () => {
      if (deleteConfirmIndex.value === null) return;
      const updated = props.items.filter(
        (_, i) => i !== deleteConfirmIndex.value,
      );
      props.onUpdate(updated);
      deleteConfirmIndex.value = null;
    };

    return () => (
      <div>
        <p class="text-sm mb-4">{t("settings.menu_pdfs.desc")}</p>

        {props.items.length === 0 ? (
          <div class="text-center py-12 bg-base-200 rounded-lg mb-4">
            <span class="material-symbols-outlined text-5xl mb-3">
              picture_as_pdf
            </span>
            <p>{t("settings.menu_pdfs.no_pdfs_title")}</p>
            <p class="text-sm mt-1">{t("settings.menu_pdfs.no_pdfs_desc")}</p>
          </div>
        ) : (
          <div class="space-y-2 mb-4">
            {props.items.map((item, index) => (
              <div
                key={`${item.url}-${index}`}
                class="flex items-center justify-between gap-3 rounded-lg bg-base-200 px-4 py-3"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <span class="material-symbols-outlined text-primary">
                    picture_as_pdf
                  </span>
                  <span class="truncate">{item.name}</span>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    class="btn btn-ghost btn-xs"
                  >
                    {t("settings.menu_pdfs.view_link")}
                  </a>
                  <button
                    type="button"
                    class="btn btn-circle btn-xs btn-error"
                    onClick={() => handleDeleteClick(index)}
                    title={t("settings.menu_pdfs.remove_button")}
                  >
                    <span class="material-symbols-outlined text-sm">
                      delete
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div class="flex flex-col sm:flex-row items-start sm:items-end gap-3 rounded-lg border border-base-300 p-4">
          <label class="form-control w-full sm:w-auto flex-1">
            <span class="label-text text-sm">
              {t("settings.menu_pdfs.name_label")}
            </span>
            <input
              type="text"
              class="input input-bordered input-sm w-full"
              placeholder={t("settings.menu_pdfs.name_placeholder")}
              value={newName.value}
              onInput={(e) => {
                newName.value = (e.target as HTMLInputElement).value;
              }}
            />
          </label>
          <label class="form-control w-full sm:w-auto flex-1">
            <span class="label-text text-sm">
              {t("settings.menu_pdfs.file_label")}
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              class="file-input file-input-bordered file-input-sm w-full"
            />
          </label>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            disabled={uploading.value}
            onClick={handleAdd}
          >
            {uploading.value ? (
              <span class="loading loading-spinner loading-xs"></span>
            ) : (
              <span class="material-symbols-outlined text-sm">add</span>
            )}
            {t("settings.menu_pdfs.add_button")}
          </button>
        </div>

        {uploadError.value && (
          <div class="alert alert-error mt-4">
            <span class="material-symbols-outlined">error</span>
            <span>{uploadError.value}</span>
          </div>
        )}

        {deleteConfirmIndex.value !== null && (
          <div class="modal modal-open">
            <div class="modal-box">
              <h3 class="font-bold text-lg text-accent mb-4">
                {t("settings.menu_pdfs.confirm_delete_title")}
              </h3>
              <p class="mb-6">{t("settings.menu_pdfs.confirm_delete_desc")}</p>
              <div class="flex gap-3 justify-end">
                <button
                  class="btn btn-ghost"
                  onClick={() => {
                    deleteConfirmIndex.value = null;
                  }}
                >
                  {t("settings.gallery.cancel_button")}
                </button>
                <button class="btn btn-error" onClick={confirmDelete}>
                  <span class="material-symbols-outlined">delete</span>
                  {t("settings.menu_pdfs.remove_button")}
                </button>
              </div>
            </div>
            <div
              class="modal-backdrop"
              onClick={() => {
                deleteConfirmIndex.value = null;
              }}
            />
          </div>
        )}
      </div>
    );
  },
});
